import { scrollIntoViewHoriz } from '../utils/horizontal-scroll.js';
import { updateScrollOffset, getTabsListHeight } from '../utils/layout-utils.js';

const PRODUCT_NAV_SCROLL_STEP = 160;
const OVERFLOW_EPSILON = 2;
const MANUAL_SCROLL_SECTION_LOCK_MS = 1000;
const MANUAL_SCROLL_TAB_LOCK_MS = 500;
const TAB_VISIBILITY_MARGIN = 96;
const SECTION_VISIBILITY_MARGIN = 48;

function getHeaderHeight() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--site-header-height');
  const value = parseFloat(raw);
  return Number.isNaN(value) ? 0 : value;
}

function normalizeHash(hash) {
  if (!hash) return '';
  return hash.startsWith('#') ? hash : `#${hash}`;
}

const isElementVisible = (element) => Boolean(element && element.getClientRects().length);

function initSingleSlider(slider) {
  const track = slider.querySelector('[data-nav-track]');
  const navSection = slider.closest('.product-navigation');
  if (!track || !navSection) return;

  const prevButton = slider.querySelector('[data-nav-arrow="prev"]');
  const nextButton = slider.querySelector('[data-nav-arrow="next"]');
  const dropdownContainer = slider.querySelector('.product-navigation__dropdown');
  const dropdownToggle = slider.querySelector('[data-nav-dropdown-toggle]');
  const dropdownPanel = slider.querySelector('[data-nav-dropdown-panel]');
  const dropdownLabel = slider.querySelector('[data-dropdown-label]');
  const dropdownLinks = slider.querySelectorAll('[data-nav-dropdown-link]');
  const links = Array.from(track.querySelectorAll('a[href^="#"]'));
  const liveRegion = slider.querySelector('[data-nav-status]');

  const sectionTargets = links
    .map((link) => {
      const href = link.getAttribute('href') || '';
      const normalized = normalizeHash(href);
      if (!normalized) return null;
      const targetId = normalized.slice(1);
      if (!targetId) return null;
      const targetEl = document.getElementById(targetId);
      if (!targetEl) return null;
      return { hash: normalized, element: targetEl };
    })
    .filter(Boolean);
  const findSectionTarget = (hash) => sectionTargets.find(({ hash: sectionHash }) => sectionHash === hash);

  const factsSection = document.querySelector('.product-header__facts');
  let threshold = window.innerHeight;
  let sliderVisible = false;
  let overflowFrame = null;
  let announceCache = '';
  let currentActiveHash = '';
  let activeScanFrame = null;
  let sectionObserver = null;
  const supportsObserver = typeof window !== 'undefined' && 'IntersectionObserver' in window;
  let allowHashUpdates = false;
  let manualScrollLockUntil = 0;
  let manualScrollTarget = null;
  let manualScrollType = null;
  let manualResetTimer = null;
  let suppressTabButtonHandler = false;

  const isManualScrollActive = () => manualScrollLockUntil && performance.now() < manualScrollLockUntil;

  const clearManualScrollState = () => {
    manualScrollLockUntil = 0;
    manualScrollTarget = null;
    manualScrollType = null;
    if (manualResetTimer) {
      window.clearTimeout(manualResetTimer);
      manualResetTimer = null;
    }
  };

const scheduleManualScrollReset = (duration, options = {}) => {
  if (duration <= 0) return;
  const { clearState = true } = options;
  if (manualResetTimer) window.clearTimeout(manualResetTimer);
  manualResetTimer = window.setTimeout(() => {
    manualScrollLockUntil = 0;
    manualResetTimer = null;
    if (clearState) {
      manualScrollTarget = null;
      manualScrollType = null;
    }
    updateActiveFromScroll();
  }, duration + 200);
};

  const setVisible = (visible) => {
    if (visible === sliderVisible) return;
    navSection.classList.toggle('product-navigation--visible', visible);
    sliderVisible = visible;
  };

  const evaluateVisibility = () => {
    const current = window.scrollY + getHeaderHeight();
    setVisible(current >= threshold);
  };

  const updateThreshold = () => {
    if (factsSection) {
      const rect = factsSection.getBoundingClientRect();
      threshold = window.scrollY + rect.bottom;
    } else {
      threshold = window.innerHeight;
    }
    evaluateVisibility();
  };

  const updateArrowState = () => {
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const hasOverflow = maxScroll > OVERFLOW_EPSILON;
    const hasLeft = hasOverflow && track.scrollLeft > OVERFLOW_EPSILON;
    const hasRight = hasOverflow && track.scrollLeft < (maxScroll - OVERFLOW_EPSILON);

    navSection.classList.toggle('product-navigation--has-left', hasLeft);
    navSection.classList.toggle('product-navigation--has-right', hasRight);

    if (prevButton) {
      prevButton.disabled = !hasLeft;
      prevButton.setAttribute('aria-disabled', hasLeft ? 'false' : 'true');
    }
    if (nextButton) {
      nextButton.disabled = !hasRight;
      nextButton.setAttribute('aria-disabled', hasRight ? 'false' : 'true');
    }
  };

  const scheduleOverflowUpdate = () => {
    if (overflowFrame) return;
    overflowFrame = window.requestAnimationFrame(() => {
      overflowFrame = null;
      updateArrowState();
    });
  };

  const scrollByDirection = (direction) => {
    const offset = direction === 'next' ? PRODUCT_NAV_SCROLL_STEP : -PRODUCT_NAV_SCROLL_STEP;
    track.scrollBy({ left: offset, behavior: 'smooth' });
  };

  if (prevButton) prevButton.addEventListener('click', () => scrollByDirection('prev'));
  if (nextButton) nextButton.addEventListener('click', () => scrollByDirection('next'));
  track.addEventListener('scroll', scheduleOverflowUpdate, { passive: true });

  const ensureLinkVisible = (link) => {
    if (!link) return;
    const item = link.closest('.product-navigation__item') || link;
    scrollIntoViewHoriz(item, track, 'center');
  };

  const findLinkByHash = (hash) => links.find((link) => link.getAttribute('href') === hash);

  const announceActive = (label) => {
    if (!liveRegion) return;
    if (!label) {
      liveRegion.textContent = '';
      announceCache = '';
      return;
    }
    if (label === announceCache) return;
    liveRegion.textContent = `${label} aktiv`;
    announceCache = label;
  };

  const scrollWithOffset = (target) => {
    if (!target) return;
    window.requestAnimationFrame(() => {
      const offset = updateScrollOffset();
      const tabListHeight = getTabsListHeight(target);
      const extraGap = window.matchMedia('(min-width: 768px)').matches ? 64 : 32;
      const tabsOffset = tabListHeight + extraGap;
      const top = window.scrollY + target.getBoundingClientRect().top - offset - tabsOffset;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    });
  };

  const setActiveLink = (hash, options = {}) => {
    const { scrollTrack = false, force = false, updateHash = false } = options;
    const normalized = normalizeHash(hash);
    if (!normalized) {
      currentActiveHash = '';
      return false;
    }

    const hasRealSection = sectionTargets.some(({ hash: sectionHash }) => sectionHash === normalized);
    if (!hasRealSection) {
      links.forEach((link) => link.classList.remove('is-active'));
      dropdownLinks.forEach((link) => link.classList.remove('is-active'));
      currentActiveHash = '';
      if (dropdownLabel && sectionTargets[0]) {
        const firstLink = findLinkByHash(sectionTargets[0].hash);
        dropdownLabel.textContent = firstLink ? firstLink.textContent.trim() : dropdownLabel.textContent;
      }
      return false;
    }

    if (!force && normalized === currentActiveHash) {
      if (scrollTrack) {
        const currentLink = findLinkByHash(normalized);
        if (currentLink) ensureLinkVisible(currentLink);
      }
      return true;
    }

    let matched = false;
    let activeLinkEl = null;
    let activeLabel = '';

    links.forEach((link) => {
      const isActive = link.getAttribute('href') === normalized;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        matched = true;
        activeLinkEl = link;
        link.setAttribute('aria-current', 'true');
        const text = link.textContent ? link.textContent.trim() : '';
        if (text) activeLabel = text;
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (dropdownLabel) {
      if (matched && activeLabel) {
        dropdownLabel.textContent = activeLabel;
      } else if (!matched && sectionTargets[0]) {
        const firstLink = findLinkByHash(sectionTargets[0].hash);
        if (firstLink) dropdownLabel.textContent = firstLink.textContent.trim();
      }
    }

    dropdownLinks.forEach((link) => {
      const isActive = link.getAttribute('href') === normalized;
      link.classList.toggle('is-active', isActive);
    });

    if (matched) {
      announceActive(activeLabel);
      currentActiveHash = normalized;
      if (updateHash && allowHashUpdates) {
        if (window.location.hash !== normalized) {
          history.replaceState(null, '', normalized);
        }
      }
    } else {
      announceActive('');
      currentActiveHash = '';
    }

    if (scrollTrack && activeLinkEl) {
      ensureLinkVisible(activeLinkEl);
      scheduleOverflowUpdate();
    }

    return matched;
  };

  const activateAndScroll = (href) => {
    const normalized = normalizeHash(href);
    if (!normalized) return;
    const targetId = normalized.slice(1);
    const targetEl = document.getElementById(targetId);
    const isTabPanel = targetEl && targetEl.matches('[role="tabpanel"]');
    const activated = setActiveLink(normalized, { scrollTrack: true, force: true, updateHash: true });
    if (!activated) return;
    allowHashUpdates = true;
    manualScrollTarget = normalized;
    manualScrollType = isTabPanel ? 'tab' : 'section';
    const lockDuration = isTabPanel ? MANUAL_SCROLL_TAB_LOCK_MS : MANUAL_SCROLL_SECTION_LOCK_MS;

    if (isTabPanel) {
      const tabButtonId = targetEl.getAttribute('aria-labelledby');
      const tabButton = tabButtonId ? document.getElementById(tabButtonId) : null;
      if (tabButton && typeof tabButton.click === 'function') {
        suppressTabButtonHandler = true;
        try {
          tabButton.click();
        } finally {
          suppressTabButtonHandler = false;
        }
      }
    }

    if (manualResetTimer) {
      window.clearTimeout(manualResetTimer);
      manualResetTimer = null;
    }
    if (lockDuration > 0) {
      manualScrollLockUntil = performance.now() + lockDuration;
      scheduleManualScrollReset(lockDuration, { clearState: false });
    } else {
      manualScrollLockUntil = 0;
    }

    scrollWithOffset(targetEl);
    if (isTabPanel) {
      updateActiveFromScroll();
    }
  };

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      activateAndScroll(link.getAttribute('href'));
    });
  });

  const handleTabButtonClick = (hash) => {
    if (suppressTabButtonHandler) return;
    const normalized = normalizeHash(hash);
    if (!normalized) return;
    allowHashUpdates = true;
    manualScrollTarget = normalized;
    manualScrollType = 'tab';
    manualScrollLockUntil = performance.now() + MANUAL_SCROLL_TAB_LOCK_MS;
    scheduleManualScrollReset(MANUAL_SCROLL_TAB_LOCK_MS, { clearState: false });
    setActiveLink(normalized, { scrollTrack: true, force: true, updateHash: true });
  };

  const attachTabButtonListeners = () => {
    sectionTargets.forEach(({ hash, element }) => {
      if (!element.matches('[role="tabpanel"]')) return;
      const buttonId = element.getAttribute('aria-labelledby');
      if (!buttonId) return;
      const tabButton = document.getElementById(buttonId);
      if (!tabButton || tabButton.dataset.productNavLinked === '1') return;
      tabButton.dataset.productNavLinked = '1';
      tabButton.addEventListener('click', () => handleTabButtonClick(hash));
    });
  };

  attachTabButtonListeners();

  const closeDropdown = () => {
    if (dropdownToggle && dropdownContainer) {
      dropdownToggle.setAttribute('aria-expanded', 'false');
      dropdownContainer.classList.remove('product-navigation__dropdown--open');
    }
  };

  const openDropdown = () => {
    if (dropdownToggle && dropdownContainer) {
      dropdownToggle.setAttribute('aria-expanded', 'true');
      dropdownContainer.classList.add('product-navigation__dropdown--open');
    }
  };

  if (dropdownToggle && dropdownPanel) {
    closeDropdown();

    dropdownToggle.addEventListener('click', () => {
      const expanded = dropdownToggle.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    document.addEventListener('click', (event) => {
      if (!slider.contains(event.target)) {
        closeDropdown();
      }
    });

    dropdownLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        closeDropdown();
        activateAndScroll(link.getAttribute('href'));
      });
    });
  }

  const handleHashChange = () => {
    if (isManualScrollActive()) return;
    const currentHash = normalizeHash(window.location.hash);
    if (currentHash) {
      setActiveLink(currentHash, { scrollTrack: true, force: true });
    }
  };

  window.addEventListener('hashchange', handleHashChange);
  window.addEventListener('popstate', handleHashChange);

  const updateActiveFromScroll = () => {
    if (!sectionTargets.length) return;
    if (!allowHashUpdates && !window.location.hash) return;
    if (isManualScrollActive()) return;
    if (activeScanFrame) return;
    activeScanFrame = window.requestAnimationFrame(() => {
      activeScanFrame = null;
      const headerOffset = getHeaderHeight() + 32;
      const referenceLine = window.scrollY + headerOffset;

      if (manualScrollType === 'tab' && manualScrollTarget) {
        const manualEntry = findSectionTarget(manualScrollTarget);
        if (manualEntry && manualEntry.element) {
          const rect = manualEntry.element.getBoundingClientRect();
          const elementTop = window.scrollY + rect.top;
          const elementHeight = rect.height || manualEntry.element.offsetHeight || 0;
          const elementBottom = elementTop + elementHeight;
          const upperBound = elementTop - TAB_VISIBILITY_MARGIN;
          const lowerBound = elementBottom + TAB_VISIBILITY_MARGIN;

          if (referenceLine < upperBound) {
            setActiveLink(manualScrollTarget, { scrollTrack: true, force: true, updateHash: false });
            return;
          }

          if (referenceLine <= lowerBound) {
            setActiveLink(manualScrollTarget, { scrollTrack: true, force: true, updateHash: true });
            return;
          }

          manualScrollTarget = null;
          manualScrollType = null;
        } else {
          clearManualScrollState();
        }
      } else if (manualScrollType === 'section' && manualScrollTarget) {
        const manualEntry = findSectionTarget(manualScrollTarget);
        if (manualEntry && manualEntry.element) {
          const rect = manualEntry.element.getBoundingClientRect();
          const elementTop = window.scrollY + rect.top;
          const elementHeight = rect.height || manualEntry.element.offsetHeight || 0;
          const elementBottom = elementTop + elementHeight;
          const upperBound = elementTop - SECTION_VISIBILITY_MARGIN;
          const lowerBound = elementBottom + SECTION_VISIBILITY_MARGIN;

          if (referenceLine < upperBound) {
            setActiveLink(manualScrollTarget, { scrollTrack: true, force: true, updateHash: false });
            return;
          }
          if (referenceLine <= lowerBound) {
            setActiveLink(manualScrollTarget, { scrollTrack: true, force: true, updateHash: true });
            return;
          }
          clearManualScrollState();
        } else {
          clearManualScrollState();
        }
      }

      let candidateHash = null;
      let candidateTop = -Infinity;

      sectionTargets.forEach(({ hash, element }) => {
        if (!isElementVisible(element)) return;
        const absoluteTop = element.getBoundingClientRect().top + window.scrollY;
        if (absoluteTop <= referenceLine && absoluteTop > candidateTop) {
          candidateTop = absoluteTop;
          candidateHash = hash;
        }
      });

      if (!candidateHash && manualScrollType === 'tab' && manualScrollTarget) {
        candidateHash = manualScrollTarget;
      } else if (!candidateHash && sectionTargets.length) {
        const fallbackTarget = sectionTargets.find(({ element }) => isElementVisible(element));
        candidateHash = fallbackTarget ? fallbackTarget.hash : sectionTargets[0].hash;
      }

      if (!candidateHash) return;

      const shouldUpdateHash = !manualScrollTarget || manualScrollTarget === candidateHash;
      setActiveLink(candidateHash, { scrollTrack: true, force: true, updateHash: shouldUpdateHash });
      if (manualScrollTarget && manualScrollTarget === candidateHash) {
        clearManualScrollState();
      }
    });
  };

  const initializeState = () => {
    allowHashUpdates = Boolean(window.location.hash);
    scheduleOverflowUpdate();
    updateThreshold();
    const firstHref = links.length ? links[0].getAttribute('href') : '';
    const firstTargetHash = sectionTargets[0]?.hash;
    const initialHash = normalizeHash(window.location.hash || firstTargetHash || firstHref);
    if (initialHash) {
      const activated = setActiveLink(initialHash, { scrollTrack: true, force: true });
      if (!activated) {
        updateActiveFromScroll();
      }
    } else {
      updateActiveFromScroll();
    }
  };

  initializeState();

  const attachObserver = () => {
    if (!supportsObserver || !sectionTargets.length) return;
    if (sectionObserver) sectionObserver.disconnect();
    const margin = `-${getHeaderHeight()}px 0px -70% 0px`;
    sectionObserver = new IntersectionObserver((entries) => {
      let best = null;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (!best || entry.boundingClientRect.top < best.boundingClientRect.top) {
          best = entry;
        }
      });
      if (isManualScrollActive()) return;
      if (best && best.target && best.target.id) {
        const hash = `#${best.target.id}`;
        const shouldUpdateHash = !manualScrollTarget || manualScrollTarget === hash;
        setActiveLink(hash, { scrollTrack: true, force: true, updateHash: shouldUpdateHash });
        if (manualScrollTarget && manualScrollTarget === hash) {
          clearManualScrollState();
        }
      }
    }, {
      root: null,
      rootMargin: margin,
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    sectionTargets.forEach(({ element }) => sectionObserver.observe(element));
  };

  attachObserver();

  const onResize = () => {
    scheduleOverflowUpdate();
    updateThreshold();
    if (supportsObserver) {
      attachObserver();
    } else {
      updateActiveFromScroll();
    }
  };

  const onScroll = () => {
    if (!allowHashUpdates && window.scrollY > getHeaderHeight()) {
      allowHashUpdates = true;
    }
    evaluateVisibility();
    if (!supportsObserver) {
      updateActiveFromScroll();
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.addEventListener('load', () => {
    scheduleOverflowUpdate();
    updateThreshold();
    updateActiveFromScroll();
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      scheduleOverflowUpdate();
      updateThreshold();
      updateActiveFromScroll();
    }).catch(() => {});
  }
}

export default function initProductNavigation() {
  const sliders = document.querySelectorAll('[data-product-navigation]');
  if (!sliders.length) return;
  sliders.forEach(initSingleSlider);
}
